import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { QuestionsForm } from '@mission.io/question-views'
import { Container } from '../../assets/styles'
import {
	ClozeDropdownIcon,
	MultiSelectIcon,
	ModelingIcon,
	FreeResponseIcon,
	MultipleChoiceIcon,
} from '../../assets/img'
import {
	updateQuestion as _updateQuestion,
	UpdateQuestionAction,
	UPDATE_QUESTION_ACTIONS,
} from '../../model/editableQuestion/actions'
import Question from '../Question'
import {
	QuestionVariant,
	variantTypes,
	updateMedia,
} from '@mission.io/question-toolkit'
import {
	EditableQuestion,
	getNewQuestion,
	MediaToUpload,
} from '../../model/editableQuestion/model'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { _tempTheme } from '../../utils/tempTheme'
import classNames from 'classnames'
import { useDimensions } from '../../utils'

type QuestionOption = {
	type: QuestionVariant
	label: string
	icon: any
}

const questionOptions: QuestionOption[] = [
	{
		type: variantTypes.MULTIPLE_CHOICE,
		label: 'Multiple Choice',
		icon: MultipleChoiceIcon,
	},
	{
		type: variantTypes.FREE_RESPONSE,
		label: 'Free Response',
		icon: FreeResponseIcon,
	},
	{
		type: variantTypes.MULTIPLE_SELECT,
		label: 'Multiple Select',
		icon: MultiSelectIcon,
	},
	{
		type: variantTypes.IMAGE_LABELING,
		label: 'Modeling',
		icon: ModelingIcon,
	},
	{
		type: variantTypes.CLOZE_DROPDOWN,
		label: 'Cloze Dropdown',
		icon: ClozeDropdownIcon,
	},
]
/**
 * Performs a deep copy on the provided question object, ensuring that local files are also copied over properly.
 * @param {EditableQuestion} question
 * @returns {EditableQuestion} copied question
 */
function copyQuestion(question: EditableQuestion): EditableQuestion {
	// We need to do some fancy logic to make sure files for the question are copied correctly
	const fileReferenceMap: { [localUrl: string]: File | Blob } = {}
	// Save file references
	// We aren't actually updating the media on the question. We are just using this function to access all points where media could be in the question
	// to make sure that local files are are not erased after JSON.parse(JSON.stringify(q))

	// @ts-expect-error updateMedia expects a ClientQuestion not an EditableQuestion, but the media locations are the same on both types
	updateMedia(question, (media: MediaToUpload) => {
		if (media.file && media.localUrl) {
			fileReferenceMap[media.localUrl] = media.file
		}
		return media
	})
	const copy = JSON.parse(JSON.stringify(question))
	if (Object.keys(fileReferenceMap).length === 0) {
		return copy
	}
	// @ts-expect-error MediaToUpload looks a little different from the MediaObj in question-toolkit because there is a file and local url.
	updateMedia(copy, (media: MediaToUpload) => {
		if (media.file && fileReferenceMap[media.localUrl]) {
			// @ts-expect-error TS2322 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			media.file = fileReferenceMap[media.localUrl]
		}
		return media
	})

	return copy
}

const getIdForIndex = (index: number) => `q-id-${index}`
export const EditQuestions = (props: QuestionsForm.Props): JSX.Element => {
	const {
		questions,
		add,
		remove,
		update,
		className,
		headerClassName,
		sideNavClassName,
	} = props
	const [selected, setSelected] = useState<number>(0)

	const [autoFocusQuestionIndex, setAutoFocusQuestionIndex] = useState(
		null as null | number
	)
	useLayoutEffect(() => {
		// only autoFocus a question for one render
		if (autoFocusQuestionIndex == null) {
			return
		}

		setAutoFocusQuestionIndex(null)
	}, [autoFocusQuestionIndex])

	const addQuestion = (optionSelected: QuestionVariant) => {
		const newIndex = questions.length
		add(getNewQuestion(optionSelected))
		setAutoFocusQuestionIndex(newIndex)
		setSelected(newIndex)
	}
	const updateQuestion = (index: number, action: UpdateQuestionAction) => {
		if (questions[index]) {
			if (action.type === UPDATE_QUESTION_ACTIONS.DELETE_QUESTION) {
				remove(index)
				return
			}
			const copy = copyQuestion(questions[index])
			if (action.type === UPDATE_QUESTION_ACTIONS.DUPLICATE_QUESTION) {
				add({ ...copy, _id: undefined })
				return
			}
			if (_updateQuestion(copy, action)) {
				copy.updated = true
				update(copy, index)
			}
		}
	}

	const questionPickerRef = useRef<HTMLDivElement>(null)
	const questionPickerHeight = useDimensions(questionPickerRef).height
	useEffect(() => {
		if (selected != null) {
			const item = document.getElementById(getIdForIndex(selected))
			if (item) {
				const y =
					item.getBoundingClientRect().top +
					window.scrollY -
					questionPickerHeight
				window.scrollTo({ top: y, behavior: 'smooth' })
			}
		}
	}, [selected])

	return (
		<Container className={className}>
			<div
				ref={questionPickerRef}
				className={classNames(
					'w-full flex mb-2 bg-brand-deep-blue rounded-lg justify-evenly p-2',
					headerClassName
				)}>
				{questionOptions.map((item: QuestionOption, index: number) => (
					<button
						key={`button-${index}`}
						onClick={() => addQuestion(item.type)}
						className="flex flex-col gap-1 items-center p-1 rounded-lg hover:brightness-90 active:brightness-75">
						<img src={item.icon} />
						<span className="font-semibold text-sm">{item.label}</span>
					</button>
				))}
			</div>
			<QuestionGroupContent>
				<div className={classNames('side-margin h-fit', sideNavClassName)}>
					{questions.map((_, index) => (
						// const TabQuestion = styled.div<ITabQuestion>`
						// 	text-align: left;
						// 	position: sticky;
						// 	top: ${({ theme }) => getAbsoluteQuestionPickerHeight(theme)}px;
						// 	font-style: normal;
						// 	font-weight: normal;
						// 	text-transform: capitalize;
						// 	cursor: pointer;
						// 	font-size: ${_tempTheme.text_base};
						// 	line-height: 19px;
						// 	display: flex;
						// 	padding-top: ${_tempTheme.spacing2};
						// 	padding-bottom: ${_tempTheme.spacing2};
						// 	border-left: ${({ $selected, theme }) =>
						// 		$selected && `${_tempTheme.spacing1} solid ${theme.primary}`};
						// 	padding-left: ${({ $selected, theme }) =>
						// 		$selected
						// 			? theme.spacing
						// 			: `${theme.spacingValue + theme.spacingHalfValue}px`};
						// `
						<button
							key={`q-label-${index}`}
							className={classNames(
								'sticky capitalize cursor-pointer flex py-2 pl-2 border-l-2',
								index === selected
									? 'border-l-brand-horizon-blue'
									: 'border-l-transparent'
							)}
							onClick={() => {
								setSelected(index)
							}}>
							Question {index + 1}
						</button>
					))}
				</div>
				<div id="questions-list-content">
					{questions.map((question, index) => (
						<QuestionWrapper
							$selected={index === selected}
							key={`q-${index}`}
							id={getIdForIndex(index)}
							onClick={() => setSelected(index)}>
							<Question
								// @ts-expect-error An EditableQuestion must be exchanged for a Question in this case
								question={question}
								teacherQuestion
								onChange={(updateAction: QuestionsForm.UpdateQuestionAction) =>
									updateQuestion(index, updateAction)
								}
								questionIndex={index}
								autoFocus={index === autoFocusQuestionIndex}
							/>
						</QuestionWrapper>
					))}
				</div>
				<div className="side-margin"></div>
			</QuestionGroupContent>
		</Container>
	)
}
export default EditQuestions

// const QUESTION_PICKER_HEIGHT = 78
// const getAbsoluteQuestionPickerHeight = (theme: any) =>
// 	QUESTION_PICKER_HEIGHT + theme.spacingValue * 2 + theme.spacing2xValue * 2

const QuestionWrapper = styled.div<{ $selected: boolean }>`
	box-shadow: ${({ $selected }) =>
		$selected
			? '0px 3px 10px 6px rgba(0, 0, 0, 0.09)'
			: '0px 2px 4px 2px rgba(0, 0, 0, 0.07)'};

	border-radius: ${_tempTheme.spacing1};
	background: ${({ theme }) => theme.baseLight};
	&:not(:last-child) {
		margin-bottom: ${_tempTheme.spacing4};
	}
`

const QuestionGroupContent = styled.div`
	display: flex;
	.side-margin {
		flex: 1;
	}
	div#questions-list-content {
		position: relative;
		padding: ${_tempTheme.spacing2};
		width: 60%;

		.extra-space {
			height: 20vh;
		}
	}
`
